import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import {
  Loader2,
  MessageSquare,
  Calendar,
  Handshake,
  Send,
  MessageCircle,
  ChevronRight,
  Activity,
} from "lucide-react";
import { crmMyWork, type CrmMyWorkResult, type WorkItem } from "@/lib/crm-mywork.functions";

export const Route = createFileRoute("/crm/my-work")({
  head: () => ({
    meta: [
      { title: "CRM My Work | What Finland" },
      {
        name: "description",
        content: "Review your CRM contacts, meetings, deals, SMM deliveries, and team chat activity in one work timeline.",
      },
      { property: "og:title", content: "CRM My Work | What Finland" },
      {
        property: "og:description",
        content: "Review CRM and SMM activity across your What Finland sales workflow.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: MyWorkPage,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <div className="p-6 text-sm text-rose-700">
        Failed to load My Work: {(error as Error).message}
        <button
          className="ml-2 underline"
          onClick={() => {
            reset();
            router.invalidate();
          }}
        >
          Retry
        </button>
      </div>
    );
  },
  notFoundComponent: () => <div className="p-6 text-sm">Not found.</div>,
});

const TYPE_META: Record<WorkItem["type"], { label: string; icon: any; tone: string }> = {
  contact: { label: "Contact Log", icon: MessageSquare, tone: "bg-sky-100 text-sky-700 border-sky-200" },
  meeting: { label: "Meeting", icon: Calendar, tone: "bg-violet-100 text-violet-700 border-violet-200" },
  deal: { label: "Deal", icon: Handshake, tone: "bg-amber-100 text-amber-800 border-amber-200" },
  smm_delivery: { label: "SMM Delivery", icon: Send, tone: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  chat: { label: "Chat", icon: MessageCircle, tone: "bg-slate-100 text-slate-700 border-slate-200" },
};

const RANGES = [
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
];

function MyWorkPage() {
  const [days, setDays] = useState(30);
  const [filter, setFilter] = useState<WorkItem["type"] | "all">("all");
  const fetcher = useServerFn(crmMyWork);
  const { data, isLoading, error } = useQuery<CrmMyWorkResult>({
    queryKey: ["crm-my-work", days],
    queryFn: () => fetcher({ data: { days } }),
  });

  const items = (data?.items ?? []).filter((i) => filter === "all" || i.type === filter);
  const counts = data?.counts;
  const loadError = error instanceof Error ? error.message : data?.error;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-slate-500">
            <Activity className="size-3.5" /> Activity
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mt-1">My Work</h1>
          <p className="text-sm text-slate-500 mt-1">Everything you've done across CRM and SMM.</p>
        </div>
        <div className="flex items-center gap-1 rounded-xl bg-slate-100 p-1">
          {RANGES.map((r) => (
            <button
              key={r.days}
              type="button"
              onClick={() => setDays(r.days)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition ${
                days === r.days ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        <StatTile label="Total" value={counts?.total ?? 0} active={filter === "all"} onClick={() => setFilter("all")} />
        <StatTile label="Contacts" value={counts?.contact ?? 0} active={filter === "contact"} onClick={() => setFilter("contact")} />
        <StatTile label="Meetings" value={counts?.meeting ?? 0} active={filter === "meeting"} onClick={() => setFilter("meeting")} />
        <StatTile label="Deals" value={counts?.deal ?? 0} active={filter === "deal"} onClick={() => setFilter("deal")} />
        <StatTile label="SMM" value={counts?.smm_delivery ?? 0} active={filter === "smm_delivery"} onClick={() => setFilter("smm_delivery")} />
        <StatTile label="Chats" value={counts?.chat ?? 0} active={filter === "chat"} onClick={() => setFilter("chat")} />
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-20 text-slate-400">
            <Loader2 className="size-5 animate-spin" />
          </div>
        ) : loadError ? (
          <div className="py-14 px-6 text-center">
            <div className="text-sm font-semibold text-rose-700">My Work Could Not Load</div>
            <p className="mx-auto mt-2 max-w-xl text-xs text-slate-500">{loadError}</p>
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 px-6 text-sm text-slate-500">
            No activity in this range yet. Log a contact, schedule a meeting, or close a deal.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {items.map((it) => {
              const meta = TYPE_META[it.type];
              const Icon = meta.icon;
              const content = (
                <div className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50 transition">
                  <div className={`size-9 rounded-xl border grid place-items-center shrink-0 ${meta.tone}`}>
                    <Icon className="size-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-slate-900 truncate">{it.title}</span>
                      <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">
                        {it.area}
                      </span>
                      {it.client_name && (
                        <span className="text-xs text-slate-500 truncate">· {it.client_name}</span>
                      )}
                    </div>
                    {it.detail && (
                      <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{it.detail}</div>
                    )}
                    <div className="text-[10px] text-slate-400 mt-1 tabular-nums">
                      {formatWorkDate(it.at)}
                    </div>
                  </div>
                  {it.link && <ChevronRight className="size-4 text-slate-300 shrink-0 mt-2" />}
                </div>
              );
              return (
                <li key={it.id}>
                  {it.link ? (
                    <Link to={it.link} className="block">
                      {content}
                    </Link>
                  ) : (
                    content
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function formatWorkDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Time unavailable";
  return date.toLocaleString();
}

function StatTile({
  label,
  value,
  active,
  onClick,
}: {
  label: string;
  value: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left rounded-xl border px-3 py-2.5 transition ${
        active
          ? "bg-slate-900 border-slate-900 text-white shadow-md"
          : "bg-white border-slate-200 hover:border-slate-300 hover:shadow-sm"
      }`}
    >
      <div className={`text-[10px] font-bold uppercase tracking-wider ${active ? "text-white/70" : "text-slate-500"}`}>
        {label}
      </div>
      <div className={`text-xl font-bold tabular-nums mt-0.5 ${active ? "text-white" : "text-slate-900"}`}>
        {value}
      </div>
    </button>
  );
}
